package dev.lingvo.ops.pages.webhooks

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.navigation.Link
import dev.lingvo.ops.api.OpsApi
import dev.lingvo.ops.pages.OpsResourcePagination
import dev.lingvo.ops.components.OpsPageHeader
import dev.lingvo.ops.components.OpsPanel
import dev.lingvo.ops.components.OpsResourceStates
import dev.lingvo.ops.components.OpsShell
import dev.lingvo.ops.components.OpsUntrustedText
import dev.lingvo.ops.state.rememberOpsPaging
import dev.lingvo.ops.state.rememberOpsResource
import dev.lingvo.shared.ops.OpsDeliveryRowDto
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCodeBlock
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.formatCount
import dev.lingvo.site.ds.formatUtc
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text

@Page("/webhooks/{webhookId}")
@Composable
fun WebhookDetailPage() {
    val webhookId = rememberPageContext().route.params["webhookId"].orEmpty()
    OpsShell("Webhooks") {
        val webhook = rememberOpsResource(webhookId, OpsApi::webhook)
        Column(Modifier.fillMaxWidth().gap(Space.XL)) {
            Link("/webhooks") { Text("Back to webhooks") }
            OpsPageHeader("Webhook endpoint", "Inspect the saved endpoint configuration. Delivery history has its own refresh control.", webhook.state.isRefreshing, webhook::refresh)
            OpsResourceStates(webhook.state, "Webhook not found", "No accessible webhook matched this id.", retry = webhook::refresh) { row ->
                OpsPanel("Endpoint", "Customer URLs remain plain text; they cannot be opened from this authenticated console.") {
                    OpsUntrustedText(row.url)
                    P { Text("${row.events.joinToString(", ")} · ${if (row.signed) "signed" else "unsigned"}") }
                }
                DeliveryHistory(row.orgId, webhookId)
            }
        }
    }
}

@Composable
private fun DeliveryHistory(orgId: String, webhookId: String) {
    val paging = rememberOpsPaging<OpsDeliveryRowDto>(orgId to webhookId)
    val resource = rememberOpsResource(Triple(orgId, webhookId, paging.cursor)) { (workspace, endpoint, cursor) ->
        OpsApi.deliveries(workspace, endpoint, cursor)
    }
    OpsPanel("Delivery history", "Keyset-paged attempts. Response content is displayed as text and cannot be copied or opened as a link.",
        actions = { LButton("Refresh deliveries", resource::refresh, variant = LButtonVariant.SECONDARY, size = LButtonSize.SM) }) {
        OpsResourceStates(resource.state, "No deliveries in this page", "No delivery attempts were returned.", retry = resource::refresh) { page ->
            page.rows.forEach { DeliveryRow(it) }
        }
        OpsResourcePagination(paging, resource.state, "deliveries")
    }
}

@Composable
private fun DeliveryRow(row: OpsDeliveryRowDto) {
    OpsPanel("${row.event} · ${formatUtc(row.createdAt)}") {
        P { Text("${formatCount(row.attempts)} attempts · ${row.bodyBytes} body bytes · ${row.status ?: "No status"}") }
        row.responseBody?.let { LCodeBlock(it.text, "Response", maxLines = 8, copyable = false) }
    }
}
